'use client'

import dynamic from 'next/dynamic'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { HomeLink } from '@/components/home-link'
import { useUnity } from '@/hooks/use-unity'
import inciBrasilSvg from '@/public/img/inci-brasil-logo-footer.svg'
// import NewsletterFooter from './newsletter-footer'
import SuportModal from './suport-modal'

interface FooterNavProps {
  workWithUs?: string | null
  logo?: string
  template: string | null
}

export default function FooterNav({
  logo,
  workWithUs,
  template,
}: FooterNavProps) {
  const SocialMedias = dynamic(
    () =>
      import(`@/components/${template}/template/footer/social-medias`).catch(
        () => {
          return Promise.resolve(() => null)
        }
      ),
    { ssr: false }
  )

  const NewsletterFooter = dynamic(
    () =>
      import(
        `@/components/${template}/template/footer/newsletter-footer`
      ).catch(() => {
        return Promise.resolve(() => null)
      }),
    { ssr: false }
  )

  const path = usePathname()

  if (path === '/conta/login' || path === '/conta/novo') {
    return null
  }

  const { unity, metadataDetails } = useUnity()
  const partnershipUnity = metadataDetails?.partnership ?? []
  const transparencyReportGoogleSecurityUnity =
    metadataDetails?.transparency_report_google_security ?? ''
  const defaultSupportEmailUnity = unity.details?.default_support_email ?? ''
  const footerImgUrlUnity = metadataDetails?.footer_image_url
  const maintainingEntityInfoUnity = metadataDetails?.maintaining_entity_info
  const isSoeducador =
    unity?.reference === 'U5071E012' || unity?.reference === 'UE49C70E1'

  return (
    <footer className="border-primary-50 border-t-[2.625rem] bg-gray-50 pb-6 font-rethink md:border-t-[1.25rem] lg:pb-10">
      <div className="container mt-4 lg:mt-10 lg:flex lg:flex-wrap lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-start justify-between gap-6 lg:gap-16">
          <div>
            <h4 className="font-bold text-base text-neutral-900">Para Você</h4>
            <ul className="mt-4 space-y-2 font-medium text-gray-500 text-sm">
              {isSoeducador && (
                <li>
                  <Link
                    href="https://blog.soeducador.com.br"
                    prefetch={false}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Blog
                  </Link>
                </li>
              )}
              <li>
                <HomeLink href="/app">Ambiente do Aluno</HomeLink>
              </li>
              <li>
                <HomeLink href="/conta/novo">Cadastre-se</HomeLink>
              </li>
              <li>
                <HomeLink href="/certificado/validar">
                  Validar Certificado
                </HomeLink>
              </li>
              <li>
                <HomeLink href="/declaracao/validar">
                  Validar Declaração
                </HomeLink>
              </li>
              <li>
                <HomeLink href="/suporte">Central de Atendimento</HomeLink>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold text-base text-neutral-900">Sobre Nós</h4>
            <ul className="mt-4 space-y-2 font-medium text-gray-500 text-sm">
              <li>
                <HomeLink href="/quem-somos">Quem Somos</HomeLink>
              </li>
              <li>
                <HomeLink href="/termos-de-uso">Termos de uso</HomeLink>
              </li>
              <li>
                <HomeLink href="/politica-de-privacidade">
                  Política de Privacidade
                </HomeLink>
              </li>
              <li>
                <HomeLink href="/politica-de-cancelamento-e-reembolso">
                  Política de Reembolso
                </HomeLink>
              </li>
            </ul>
          </div>
          {workWithUs && (
            <div>
              <h4 className="font-bold text-base text-neutral-900">
                Cresça conosco
              </h4>
              <ul className="mt-4 space-y-2 font-medium text-gray-500 text-sm">
                {/* <li>
                    <Link href="https://inci.beeviral.app/cadastro/indique?utm_source=site&utm_medium=footer" prefetch={false}>Seja um Afiliado</Link>
                  </li> */}
                <li>
                  <HomeLink href={workWithUs} prefetch={false}>
                    Trabalhe conosco
                  </HomeLink>
                </li>
              </ul>
            </div>
          )}
        </div>
        <div className="mt-12 hidden w-full bg-gray-200 lg:order-2 lg:block lg:h-[2px]" />
        <Link
          className="mx-auto mt-10 hidden lg:order-2 lg:mx-0 lg:mt-7 lg:block"
          href="/"
        >
          <Image alt="Logo" height={33} src={logo || ''} width={220} />
        </Link>
        <div className="mt-9 flex flex-wrap items-center justify-between gap-1 gap-y-4 md:gap-0 md:gap-y-0 lg:order-2 lg:mt-7">
          <SocialMedias />
          {/* <div className="lg:hidden">
            <DropdownLanguage />
          </div> */}
        </div>
        <div className="mt-14 mb-10 lg:mt-0 lg:mb-0 lg:max-w-[20rem]">
          <div className="mb-4 flex flex-col items-center justify-center">
            <SuportModal
              defaultSupportEmailUnity={defaultSupportEmailUnity ?? ''}
            >
              <button
                className="rounded-full bg-red-500 px-14 py-3 text-center font-medium text-white hover:cursor-pointer"
                type="button"
              >
                PRECISA DE AJUDA?
                <small className="block font-normal">Clique aqui</small>
              </button>
            </SuportModal>

            <div className="mt-4 flex flex-col items-center gap-x-2 gap-y-4 md:flex-row">
              <Link
                href={transparencyReportGoogleSecurityUnity}
                rel="noreferrer"
                target="_blank"
              >
                <Image
                  alt=""
                  className="w-36"
                  height={50}
                  src="/img/selo-google-site-seguro-new.webp"
                  width={50}
                />
              </Link>
              {/* {abedUnity?.exists && (
                  <Link
                    href={abedUnity.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Image src={abedUnity.imgUrl} alt={abedUnity.name} width={79.88} height={69.08} />
                  </Link>
              )} */}

              {partnershipUnity.map(item => {
                if (item?.exists) {
                  return (
                    <Link
                      href={item.url ?? ''}
                      key={item.name}
                      rel="noreferrer"
                      target="_blank"
                    >
                      <Image
                        alt={item.name ?? ''}
                        height={22.98}
                        src={item.imgUrl ?? ''}
                        width={107.11}
                      />
                    </Link>
                  )
                }
              })}
            </div>
          </div>

          <NewsletterFooter />
        </div>
        <Link className="lg:hidden" href="/">
          <Image alt="Logo" height={32} src={logo ?? ''} width={214.67} />
        </Link>
        <div className="mt-9 flex w-full items-end justify-between lg:order-2 lg:mt-0">
          <div className="mx-auto max-w-56 flex-col space-y-3.5 lg:mx-0 lg:mt-24 lg:flex lg:max-w-full lg:flex-row lg:items-center lg:gap-3 lg:space-y-0">
            <Image
              alt="Logo"
              className="mx-auto"
              height={26.216}
              src={footerImgUrlUnity ?? inciBrasilSvg}
              width={103.483}
            />
            <span className="text-gray-500 text-xs">
              © {new Date().getFullYear()}{' '}
              {maintainingEntityInfoUnity ??
                'INCI - INSTITUTO NACIONAL DE APERFEIÇOAMENTO PROFISSIONAL - CNPJ: 36.692.668/0001-94'}
            </span>
          </div>
        </div>
      </div>
    </footer>
  )
}
